/**
 * Custom Mega Menu JavaScript
 * Place this file in: assets/custom-mega-menu.js
 */

class CustomMegaMenu {
  constructor() {
    this.menuElement = document.querySelector('[data-mega-menu]');
    if (!this.menuElement) return;

    this.menuToggle = this.menuElement.querySelector('[data-menu-toggle]');
    this.menuNav = this.menuElement.querySelector('[data-menu-nav]');
    this.menuClose = this.menuElement.querySelector('[data-menu-close]');
    this.menuOverlay = this.menuElement.querySelector('[data-menu-overlay]');
    this.menuTitle = this.menuElement.querySelector('[data-menu-title]');
    this.panels = this.menuElement.querySelectorAll('[data-menu-panel]');
    this.desktopTriggers = this.menuElement.querySelectorAll('[data-desktop-menu-trigger]');
    this.panelHistory = [];
    this.activeDesktopTrigger = null;

    this.init();
  }

  init() {
    this.bindEvents();
  }

  bindEvents() {
    // Mobile: Toggle menu open/close
    if (this.menuToggle) {
      this.menuToggle.addEventListener('click', () => this.toggleMenu());
    }

    if (this.menuClose) {
      this.menuClose.addEventListener('click', () => this.closeMenu());
    }

    if (this.menuOverlay) {
      this.menuOverlay.addEventListener('click', () => this.closeMenu());
    }

    // Desktop: Handle desktop navigation clicks
    this.desktopTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => this.handleDesktopTriggerClick(e));
    });

    // Handle parent link clicks (drill down within sliding panel)
    const parentLinks = this.menuElement.querySelectorAll('[data-menu-parent]');
    parentLinks.forEach(link => {
      link.addEventListener('click', (e) => this.handleParentClick(e));
    });

    // Handle back button clicks
    const backButtons = this.menuElement.querySelectorAll('[data-menu-back]');
    backButtons.forEach(button => {
      button.addEventListener('click', () => this.navigateBack());
    });

    // Handle collection links (close menu on click)
    const collectionLinks = this.menuElement.querySelectorAll('.custom-mega-menu__link:not([data-menu-parent])');
    collectionLinks.forEach(link => {
      // Only close menu if it's an actual link (not #)
      if (link.getAttribute('href') !== '#' && link.getAttribute('href')) {
        link.addEventListener('click', () => {
          setTimeout(() => this.closeMenu(), 100);
        });
      }
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isMenuOpen()) {
        if (this.panelHistory.length > 0) {
          this.navigateBack();
        } else {
          this.closeMenu();
        }
      }
    });

    // Prevent body scroll when menu is open
    this.preventBodyScroll();
  }

  handleDesktopTriggerClick(e) {
    e.preventDefault();
    const trigger = e.currentTarget;
    const panelId = trigger.getAttribute('data-desktop-menu-trigger');

    // If clicking the same trigger, close the menu
    if (this.activeDesktopTrigger === trigger && this.isMenuOpen()) {
      this.closeMenu();
      return;
    }

    // Open menu and navigate to the panel
    this.activeDesktopTrigger = trigger;

    // Remove active class from all triggers
    this.desktopTriggers.forEach(t => t.classList.remove('active'));

    // Add active class to clicked trigger
    trigger.classList.add('active');

    // Reset to main menu first, then navigate to the specific panel
    this.resetToMainMenu();
    this.openMenu();

    // Navigate to the panel after a short delay to allow menu to open
    setTimeout(() => {
      this.navigateToPanel(panelId);
    }, 50);
  }

  toggleMenu() {
    if (this.isMenuOpen()) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  openMenu() {
    this.menuElement.classList.add('custom-mega-menu--open');
    document.body.classList.add('custom-mega-menu-open');

    // Update header title based on active panel
    const activePanel = this.menuElement.querySelector('.custom-mega-menu__panel--active');
    if (activePanel) {
      this.updateHeaderTitle(activePanel);
    }

    // Focus management for accessibility
    setTimeout(() => {
      const firstLink = this.menuElement.querySelector('.custom-mega-menu__panel--active .custom-mega-menu__link');
      if (firstLink) {
        firstLink.focus();
      }
    }, 300);
  }

  closeMenu() {
    this.menuElement.classList.remove('custom-mega-menu--open');
    document.body.classList.remove('custom-mega-menu-open');

    // Remove active state from desktop triggers
    this.desktopTriggers.forEach(t => t.classList.remove('active'));
    this.activeDesktopTrigger = null;

    // Reset to main menu after close animation
    setTimeout(() => {
      this.resetToMainMenu();
    }, 300);

    // Return focus to toggle button
    if (this.menuToggle) {
      this.menuToggle.focus();
    }
  }

  isMenuOpen() {
    return this.menuElement.classList.contains('custom-mega-menu--open');
  }

  handleParentClick(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const panelId = link.getAttribute('data-menu-parent');

    this.navigateToPanel(panelId);
  }

  navigateToPanel(panelId) {
    const currentPanel = this.menuElement.querySelector('.custom-mega-menu__panel--active');
    const targetPanel = this.menuElement.querySelector(`[data-panel-id="${panelId}"]`);

    if (!targetPanel) {
      console.warn(`Panel with id "${panelId}" not found`);
      return;
    }

    // Add current panel to history
    if (currentPanel) {
      this.panelHistory.push(currentPanel);
      currentPanel.classList.remove('custom-mega-menu__panel--active');
      currentPanel.classList.add('custom-mega-menu__panel--previous');
    }

    // Activate target panel
    targetPanel.classList.add('custom-mega-menu__panel--active');
    targetPanel.classList.remove('custom-mega-menu__panel--previous');

    // Update header title
    this.updateHeaderTitle(targetPanel);

    // Show back button when navigating from a level-1 panel (desktop opened panels)
    // This ensures back button appears when drilling down deeper
    const backButton = targetPanel.querySelector('.custom-mega-menu__back--hide-on-desktop-open');
    if (backButton && this.panelHistory.length > 1) {
      backButton.classList.remove('custom-mega-menu__back--hide-on-desktop-open');
    }

    // Scroll to top of new panel
    targetPanel.scrollTop = 0;

    // Focus first link in new panel
    setTimeout(() => {
      const firstLink = targetPanel.querySelector('.custom-mega-menu__link');
      if (firstLink) {
        firstLink.focus();
      }
    }, 100);
  }

  navigateBack() {
    if (this.panelHistory.length === 0) return;

    const currentPanel = this.menuElement.querySelector('.custom-mega-menu__panel--active');
    const previousPanel = this.panelHistory.pop();

    if (currentPanel) {
      currentPanel.classList.remove('custom-mega-menu__panel--active');
    }

    if (previousPanel) {
      previousPanel.classList.remove('custom-mega-menu__panel--previous');
      previousPanel.classList.add('custom-mega-menu__panel--active');

      // Update header title
      this.updateHeaderTitle(previousPanel);

      // Scroll back to previous position (or top)
      previousPanel.scrollTop = 0;

      // Focus management
      setTimeout(() => {
        const backButton = previousPanel.querySelector('[data-menu-back]');
        if (backButton) {
          backButton.focus();
        } else {
          const firstLink = previousPanel.querySelector('.custom-mega-menu__link');
          if (firstLink) {
            firstLink.focus();
          }
        }
      }, 100);
    }
  }

  resetToMainMenu() {
    // Remove all active states
    this.panels.forEach(panel => {
      panel.classList.remove('custom-mega-menu__panel--active');
      panel.classList.remove('custom-mega-menu__panel--previous');

      // Reset back button hiding for desktop-opened panels
      const backButton = panel.querySelector('.custom-mega-menu__back');
      if (backButton && panel.getAttribute('data-level') === '1') {
        backButton.classList.add('custom-mega-menu__back--hide-on-desktop-open');
      }
    });

    // Activate first panel (main menu)
    const mainPanel = this.menuElement.querySelector('[data-level="0"]');
    if (mainPanel) {
      mainPanel.classList.add('custom-mega-menu__panel--active');
    }

    // Reset header title to "Menu"
    if (this.menuTitle) {
      this.menuTitle.textContent = 'Menu';
    }

    // Clear history
    this.panelHistory = [];

    // Reset all panel scroll positions
    this.panels.forEach(panel => {
      panel.scrollTop = 0;
    });
  }

  updateHeaderTitle(panel) {
    if (!this.menuTitle) return;

    // Get the panel title from the panel-title element or use default
    const panelTitleElement = panel.querySelector('.custom-mega-menu__panel-title');
    if (panelTitleElement) {
      this.menuTitle.textContent = panelTitleElement.textContent;
    } else {
      // For main menu (level 0), use "Menu"
      this.menuTitle.textContent = 'Menu';
    }
  }

  preventBodyScroll() {
    // Store original body overflow
    let scrollY = 0;

    const lockScroll = () => {
      scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    };

    const unlockScroll = () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      window.scrollTo(0, scrollY);
    };

    // Listen for class changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          if (document.body.classList.contains('custom-mega-menu-open')) {
            lockScroll();
          } else {
            unlockScroll();
          }
        }
      });
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new CustomMegaMenu();
  });
} else {
  new CustomMegaMenu();
}

// Re-initialize on Shopify section render (theme editor)
document.addEventListener('shopify:section:load', () => {
  new CustomMegaMenu();
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomMegaMenu;
}
